import React, { useMemo, useState } from "react";
import { X, ShieldCheck, ArrowRight, ExternalLink, Lock } from "lucide-react";
import IntegrationIcon from "@/components/IntegrationIcon";

/**
 * Luminance helper — mirrors the one in IntegrationIcon so the OAuth header +
 * primary button pick a readable foreground for BOTH dark and light brand
 * colors (Ramp #F1E7CF, DocuSign #FFCC22 etc. would render white-on-white
 * without this).
 */
function isDarkHex(hex) {
    if (!hex || typeof hex !== "string") return true;
    const c = hex.replace("#", "");
    if (c.length !== 6) return true;
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    const L = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return L < 0.55;
}

/**
 * ConnectorOAuthModal
 * Mock OAuth consent screen. Styled to look credible per the Phase-1 Addendum
 * P0 requirement: brand-colored header per integration, scopes list,
 * Authorize + Cancel buttons. No real redirect — clicking Authorize calls
 * the provided onAuthorize() (which hits the mock POST connect endpoint).
 */
export default function ConnectorOAuthModal({
    integration,
    workerName,
    companyName,
    onCancel,
    onAuthorize,
    busy = false,
}) {
    const [checkedScopes, setCheckedScopes] = useState(
        () => new Set((integration?.default_scopes || []).map((_, i) => i))
    );

    if (!integration) return null;

    const toggle = (i) => {
        const s = new Set(checkedScopes);
        if (s.has(i)) s.delete(i);
        else s.add(i);
        setCheckedScopes(s);
    };

    const authorize = () => {
        const scopes = (integration.default_scopes || []).filter((_, i) =>
            checkedScopes.has(i)
        );
        onAuthorize?.(scopes);
    };

    const dark = isDarkHex(integration.brand_color);
    const brandFg = dark ? "#ffffff" : "#0f172a";
    const brandFgMuted = dark ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.7)";
    const brandFgHairline = dark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.08)";

    return (
        <div
            className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4"
            data-testid="oauth-modal"
        >
            <div className="wfos-card w-full max-w-[520px] overflow-hidden shadow-2xl">
                {/* Brand-color header */}
                <div
                    className="relative px-6 pt-6 pb-8"
                    style={{ backgroundColor: integration.brand_color, color: brandFg }}
                >
                    <button
                        onClick={onCancel}
                        data-testid="oauth-close"
                        className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-black/5"
                        style={{ color: brandFgMuted }}
                    >
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                    <div className="flex items-center gap-3">
                        <IntegrationIcon
                            integration={integration}
                            size="lg"
                            withRing
                            testid="oauth-brand-icon"
                            className="border"
                        />
                        <div className="min-w-0">
                            <div
                                className="text-[11px] font-semibold uppercase tracking-widest"
                                style={{ color: brandFgMuted }}
                            >
                                Authorize · Mock OAuth
                            </div>
                            <div
                                className="text-lg font-semibold truncate"
                                style={{ color: brandFg }}
                                data-testid="oauth-brand-name"
                            >
                                {integration.name}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-5">
                    <div className="text-[13px] text-gray-700 leading-relaxed">
                        <b>{workerName || "Your AI worker"}</b> at <b>{companyName || "your workspace"}</b> is requesting access to your <b>{integration.name}</b> account.
                    </div>

                    <div className="mt-5">
                        <div className="wfos-label mb-2 flex items-center gap-1.5">
                            <ShieldCheck className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} />
                            Permissions requested
                        </div>
                        <ul className="space-y-2" data-testid="oauth-scopes-list">
                            {(integration.default_scopes || []).map((s, i) => (
                                <li key={i} className="flex items-start gap-2.5">
                                    <input
                                        type="checkbox"
                                        checked={checkedScopes.has(i)}
                                        onChange={() => toggle(i)}
                                        data-testid={`oauth-scope-${i}`}
                                        className="mt-1 h-3.5 w-3.5 accent-[#16a34a]"
                                    />
                                    <div className="text-[13px] text-gray-800 leading-relaxed">
                                        {s}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-5 rounded-lg bg-[#fafaf7] border border-[#e8e8e3] p-3 text-[11px] text-gray-500 flex items-start gap-2">
                        <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
                        <span>
                            This is a <b>demo OAuth flow</b> — no real connection is made and no credentials leave Nexus One. Connecting simply enables the "Lives in" strip and simulates capability handoff to this worker.
                        </span>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6 pt-2 flex items-center justify-between gap-3">
                    <a
                        href={`https://${integration.website || "example.com"}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-900"
                        onClick={(e) => e.preventDefault()}
                    >
                        Visit {integration.name} <ExternalLink className="w-3 h-3" strokeWidth={2} />
                    </a>
                    <div className="flex gap-2">
                        <button
                            onClick={onCancel}
                            disabled={busy}
                            data-testid="oauth-cancel"
                            className="rounded-lg border border-[#e8e8e3] bg-white px-3 h-9 text-sm text-gray-800 hover:border-[#d6d6cf] disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={authorize}
                            disabled={busy || checkedScopes.size === 0}
                            data-testid="oauth-authorize"
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-sm font-medium disabled:opacity-50"
                            style={{
                                backgroundColor: integration.brand_color,
                                color: brandFg,
                                border: dark ? "none" : `1px solid ${brandFgHairline}`,
                            }}
                        >
                            {busy ? "Authorizing…" : (<>Authorize <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></>)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
